'use client';

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Customer } from '@/lib/shopify/types';

interface AuthContextType {
  customer: Customer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; errors: string[] }>;
  register: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ) => Promise<{ success: boolean; errors: string[] }>;
  logout: () => Promise<void>;
  refreshCustomer: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = 'shopify_customer_token';
const AUTH_TOKEN_EXPIRY_KEY = 'shopify_customer_token_expiry';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if token is still valid
  const isTokenValid = useCallback(() => {
    const expiry = localStorage.getItem(AUTH_TOKEN_EXPIRY_KEY);
    if (!expiry) return false;
    return new Date(expiry) > new Date();
  }, []);

  // Fetch customer data
  const fetchCustomer = useCallback(async (token: string) => {
    try {
      const response = await fetch('/api/customer', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer');
      }

      const data = await response.json();
      return data.customer;
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);

      if (token && isTokenValid()) {
        const customerData = await fetchCustomer(token);
        if (customerData) {
          setCustomer(customerData);
        } else {
          // Token is invalid, clear it
          localStorage.removeItem(AUTH_TOKEN_KEY);
          localStorage.removeItem(AUTH_TOKEN_EXPIRY_KEY);
        }
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, [fetchCustomer, isTokenValid]);

  // Login
  const login = useCallback(
    async (email: string, password: string): Promise<{ success: boolean; errors: string[] }> => {
      setIsLoading(true);

      try {
        const response = await fetch('/api/customer/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (data.errors && data.errors.length > 0) {
          setIsLoading(false);
          return { success: false, errors: data.errors };
        }

        if (data.token) {
          localStorage.setItem(AUTH_TOKEN_KEY, data.token.accessToken);
          localStorage.setItem(AUTH_TOKEN_EXPIRY_KEY, data.token.expiresAt);

          // Fetch customer data
          const customerData = await fetchCustomer(data.token.accessToken);
          if (customerData) {
            setCustomer(customerData);
          }

          setIsLoading(false);
          return { success: true, errors: [] };
        }

        setIsLoading(false);
        return { success: false, errors: ['Login failed'] };
      } catch (error) {
        console.error('Login error:', error);
        setIsLoading(false);
        return { success: false, errors: ['An unexpected error occurred'] };
      }
    },
    [fetchCustomer]
  );

  // Register
  const register = useCallback(
    async (
      email: string,
      password: string,
      firstName?: string,
      lastName?: string
    ): Promise<{ success: boolean; errors: string[] }> => {
      setIsLoading(true);

      try {
        const response = await fetch('/api/customer/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, firstName, lastName }),
        });

        const data = await response.json();

        if (data.errors && data.errors.length > 0) {
          setIsLoading(false);
          return { success: false, errors: data.errors };
        }

        if (data.success) {
          // Auto-login after registration
          const loginResult = await login(email, password);
          return loginResult;
        }

        setIsLoading(false);
        return { success: false, errors: ['Registration failed'] };
      } catch (error) {
        console.error('Registration error:', error);
        setIsLoading(false);
        return { success: false, errors: ['An unexpected error occurred'] };
      }
    },
    [login]
  );

  // Logout
  const logout = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    if (token) {
      try {
        await fetch('/api/customer/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_EXPIRY_KEY);
    setCustomer(null);
  }, []);

  // Refresh customer data
  const refreshCustomer = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);

    if (token && isTokenValid()) {
      const customerData = await fetchCustomer(token);
      if (customerData) {
        setCustomer(customerData);
      }
    }
  }, [fetchCustomer, isTokenValid]);

  // Get current access token
  const getAccessToken = useCallback(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(AUTH_TOKEN_KEY);
    }
    return null;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        customer,
        isLoading,
        isAuthenticated: !!customer,
        accessToken: getAccessToken(),
        login,
        register,
        logout,
        refreshCustomer,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
